import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Alert,
  Fade,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  InfoOutlined,
  DeleteOutline,
  SaveOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

function CoverPhoto() {
  const theme = useTheme();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const token = localStorage.getItem('jwttoken');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get('https://tdtlworld.com/dss-kaas-backend/lms/user/', {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userCoverPhoto = response.data.cover_photo;
        if (userCoverPhoto) {
          setCoverPhoto(userCoverPhoto);
          setPreview(`data:image/png;base64,${userCoverPhoto}`);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [token]);

  const handleDrag = (e) => {
    e.preventDefault();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files.length === 1) {
      handleFileUpload(e.target.files[0]);
    } else {
      Swal.fire('Warning!', 'Please select only one file.', 'warning');
    }
  };

  const handleFileUpload = (file) => {
    setSelectedFile(file);
    const filePreview = URL.createObjectURL(file);
    setPreview(filePreview);

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1];
      setCoverPhoto(`data:image/png;base64,${base64String}`);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (coverPhoto) {
      const data = { cover_photo: coverPhoto.includes(',') ? coverPhoto.split(',')[1] : coverPhoto };
      try {
        const response = await axios.patch(
          'https://tdtlworld.com/dss-kaas-backend/lms/user/',
          data,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );

        if (response.data.cover_photo) {
          setPreview(`data:image/png;base64,${response.data.cover_photo}`);
          Swal.fire({
            title: 'Success!',
            text: 'Cover photo saved successfully!',
            icon: 'success',
            confirmButtonColor: '#D4AF37',
          });
        }
      } catch (error) {
        Swal.fire('Error!', 'Failed to save cover photo.', 'error');
      }
    }
  };

  const handleDelete = async () => {
    try {
      await axios.patch(
        'https://tdtlworld.com/dss-kaas-backend/lms/user/',
        { cover_photo: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedFile(null);
      setPreview(null);
      setCoverPhoto(null);
      Swal.fire({ title: 'Success!', text: 'Cover photo deleted successfully.', icon: 'success', confirmButtonColor: '#D4AF37' });
    } catch (error) {
      Swal.fire('Error!', 'Failed to delete cover photo.', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 4, textAlign: 'center' }}>
        Cover Photo
      </Typography>

      <Alert icon={<InfoOutlined />} severity="info" sx={{ mb: 4, borderRadius: '12px', bgcolor: alpha(theme.palette.info.main, 0.1) }}>
        Your Cover Photo will be used to customize the header of your profile. For best results, use 1950x450px.
      </Alert>

      <Paper
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          minHeight: 250,
          p: 0,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          bgcolor: dragActive ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
          borderRadius: '16px',
          transition: '0.3s',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        component="label"
      >
        <input type="file" hidden accept="image/*" onChange={handleChange} />
        
        {preview ? (
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <img 
              src={preview} 
              alt="Cover Preview" 
              style={{ width: '100%', height: '250px', objectFit: 'cover', display: 'block' }} 
            />
            <Box sx={{ 
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
              bgcolor: 'rgba(0,0,0,0.3)', opacity: 0, '&:hover': { opacity: 1 }, 
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.3s' 
            }}>
              <CloudUpload sx={{ color: '#fff', fontSize: '3rem' }} />
            </Box>
          </Box>
        ) : (
          <Stack spacing={1} alignItems="center">
            <CloudUpload sx={{ fontSize: '3.5rem', color: 'primary.main', mb: 1 }} />
            <Typography variant="body1" fontWeight={600}>
              Drop your cover image here or <Box component="span" sx={{ color: 'primary.main' }}>select a file</Box>
            </Typography>
          </Stack>
        )}
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<SaveOutlined />}
          onClick={handleSave}
          disabled={!selectedFile}
          sx={{ py: 1.5, borderRadius: '50px', fontWeight: 800 }}
        >
          Save Changes
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          size="large"
          startIcon={<DeleteOutline />}
          onClick={handleDelete}
          disabled={!coverPhoto}
          sx={{ py: 1.5, borderRadius: '50px', fontWeight: 800, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
        >
          Delete Cover Photo
        </Button>
      </Stack>
    </Box>
  );
}

export default CoverPhoto;
